import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { ArrowLeft, Target, Plus, ChevronDown, FileText, Globe, DownloadCloud } from 'lucide-preact'
import { campaignApi } from '../../api/campaign'
import { CampaignListModal } from './campaignList/CampaignListModal'
import { CampaignTabbedContent } from './CampaignTabbedContent'
import { NewWebPanel, type NewWebPanelMode } from '@/components/webs/NewWebPanel'
import { useBigMarkerIntegration } from '@/hooks/useBigMarkerIntegration'
import { useUserProfile } from '@/hooks/useUserProfile'
import type { CampaignViewDto } from '../../types/campaign'
import type { AudienceSearchResultDto } from '@/types/audience'
import { BRAND_CLASSES } from '../../constants/theme'
import { usePageHeader } from '@/hooks/usePageHeader'
import { routes } from '@/utils/routes'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'

interface CampaignDetailProps {
  clientCompanyId: string
  selectedWorkspaceId: string
  campaignId: string
}

export function CampaignDetail({ clientCompanyId, selectedWorkspaceId, campaignId }: CampaignDetailProps) {
  const location = useLocation()
  const { setHeader } = usePageHeader()
  const { getWorkspaceCapabilities } = useUserProfile()
  const [campaign, setCampaign] = useState<CampaignViewDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCampaignListModalOpen, setIsCampaignListModalOpen] = useState(false)
  // Null when closed; otherwise which mode the panel opened in. One piece of state rather than two
  // that can disagree about whether an import panel is showing.
  const [newWebPanelMode, setNewWebPanelMode] = useState<NewWebPanelMode | null>(null)
  // Bumped after a "Save & add another" so the webs list behind the panel picks up the new row.
  const [websRefreshKey, setWebsRefreshKey] = useState(0)
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false)
  const { updateContext } = useAIChatContextUpdater()
  const { hasBigMarker } = useBigMarkerIntegration(clientCompanyId)

  // Reset AI context to campaign-level View mode when modals close
  const resetAIContext = () => {
    updateContext({
      campaignId,
      location: ChatLocations.CampaignDetails,
      entityType: ChatEntityTypes.Campaign,
      entityId: campaignId,
      viewMode: ChatViewModes.View,
      availableFields: [],
      availableActions: [],
    })
  }

  // Get workspace capabilities from server-computed flags
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, selectedWorkspaceId)
  // Can create if user can edit (canEdit includes admin and editor roles)
  const canCreate = canEdit

  useEffect(() => {
    loadCampaign()
  }, [campaignId])

  // Set static breadcrumbs (avoid data-dependent names to reduce flicker)
  useEffect(() => {
    const r = routes.scoped(clientCompanyId, selectedWorkspaceId)
    setHeader({
      items: [
        { text: 'Campaigns', href: r.campaignsIndex() },
        { text: 'Campaign' },
      ]
    })
    return () => { }
  }, [clientCompanyId, selectedWorkspaceId, campaignId])

  const loadCampaign = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // For now, we'll simulate finding the campaign from the campaigns list
      // TODO: Replace with actual getCampaignById API call when available
      const response = await campaignApi.getCampaignsByWorkspace(selectedWorkspaceId)
      const foundCampaign = response.data?.find(c => c.Id === campaignId)

      if (foundCampaign) {
        setCampaign(foundCampaign)
      } else {
        setError('Campaign not found')
      }
    } catch (err) {
      setError('Failed to load campaign details')
      console.error('Error loading campaign:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackClick = () => {
    location.route(routes.scoped(clientCompanyId, selectedWorkspaceId).campaignsIndex())
  }

  const handleCampaignUpdated = (updatedCampaign: CampaignViewDto) => {
    setCampaign(updatedCampaign)
  }

  const handleNewCampaignListClick = () => {
    setIsCreateMenuOpen(false)
    setIsCampaignListModalOpen(true)
  }

  const handleNewWebClick = () => {
    setIsCreateMenuOpen(false)
    setNewWebPanelMode('create')
  }

  const handleImportFromBigMarkerClick = () => {
    setIsCreateMenuOpen(false)
    setNewWebPanelMode('import')
  }

  const [latestAudienceFile, setLatestAudienceFile] = useState<AudienceSearchResultDto | null>(null)
  const handleCampaignListSaved = (newAudienceFile: AudienceSearchResultDto) => {
    setIsCampaignListModalOpen(false)
    resetAIContext()
    // Optimistically set latest audience for AudienceTab append
    setLatestAudienceFile(newAudienceFile)
  }

  // Close create menu on outside click
  useEffect(() => {
    if (!isCreateMenuOpen) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest?.('#create-dropdown')) {
        setIsCreateMenuOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [isCreateMenuOpen])

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white">Campaign</h1>
            <p className="text-slate-600 dark:text-slate-300">Campaign Details & Analytics</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-32 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" />
          <div className="h-64 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[24rem]">
          <div className="text-center max-w-md mx-auto">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-primary-500/20 to-primary-400/10 blur-sm" />
              <div className="relative w-full h-full rounded-2xl bg-primary-50 dark:bg-primary-900/10 flex items-center justify-center border border-primary-200/60 dark:border-primary-800/40 shadow-inner">
                <Target className="w-9 h-9 text-primary-600 dark:text-primary-300" />
              </div>
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white mb-3">
              {error || 'Campaign Not Found'}
            </h3>
            <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              The campaign you're looking for could not be found or may have been removed.
            </p>
            <div>
              <button
                onClick={handleBackClick}
                className={`${BRAND_CLASSES.BUTTON.PRIMARY} inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-900`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Campaigns
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Persistent breadcrumbs are rendered by Home via context */}
      <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white break-words line-clamp-2 hover:line-clamp-none transition-all duration-200 cursor-pointer max-w-[58ch] md:max-w-[54ch]"
            title={campaign.Name}
            onClick={e => {
              const el = e.currentTarget;
              if (el.classList.contains('line-clamp-2')) {
                el.classList.remove('line-clamp-2');
              } else {
                el.classList.add('line-clamp-2');
              }
            }}
          >
            {campaign.Name}
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">Campaign Details & Analytics</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {canCreate && (
            <div className="relative" id="create-dropdown">
              <button
                onClick={() => setIsCreateMenuOpen(v => !v)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${isCreateMenuOpen
                  ? 'bg-primary-600 text-white'
                  : 'bg-primary-600 text-white hover:bg-primary-700'}`}
              >
                <Plus className="w-4 h-4" />
                New
                <ChevronDown className="w-4 h-4" />
              </button>
              {isCreateMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 overflow-hidden">
                  <button
                    onClick={handleNewCampaignListClick}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <FileText className="w-4 h-4" />
                    New Audience
                  </button>
                  <button
                    onClick={handleNewWebClick}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <Globe className="w-4 h-4" />
                    New Web
                  </button>
                  {/* Hidden rather than disabled when BigMarker is not connected — a permanently greyed
                      item is noise for the customers who will never connect one. */}
                  {hasBigMarker && (
                    <button
                      onClick={handleImportFromBigMarkerClick}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      <DownloadCloud className="w-4 h-4" />
                      Import from BigMarker
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabbed Content (Analytics, Assets, Details) */}
      <CampaignTabbedContent
        campaign={campaign}
        audienceAppend={latestAudienceFile}
        clientCompanyId={clientCompanyId}
        selectedWorkspaceId={selectedWorkspaceId}
        onCampaignUpdated={handleCampaignUpdated}
        websRefreshKey={websRefreshKey}
      />

      {/* Audience Modal */}
      <CampaignListModal
        isOpen={isCampaignListModalOpen}
        onClose={() => { setIsCampaignListModalOpen(false); resetAIContext() }}
        campaignId={campaignId}
        clientCompanyId={clientCompanyId}
        selectedWorkspaceId={selectedWorkspaceId}
        onCampaignListSaved={handleCampaignListSaved}
        campaignStartDate={campaign.StartDate}
      />

      {/* New Web panel - only render when open to avoid unnecessary API calls */}
      {newWebPanelMode && (
        <NewWebPanel
          isOpen
          mode={newWebPanelMode}
          onClose={() => { setNewWebPanelMode(null); resetAIContext() }}
          campaignId={campaignId}
          campaignName={campaign.Name}
          clientCompanyId={clientCompanyId}
          workspaceId={selectedWorkspaceId}
          onRequestNewAudience={() => {
            setNewWebPanelMode(null)
            setIsCampaignListModalOpen(true)
          }}
          onWebCreated={(_web, options) => {
            // "Save & add another" keeps the panel up for the next web, so only refresh behind it.
            setWebsRefreshKey(key => key + 1)
            if (options?.keepOpen) return
            setNewWebPanelMode(null)
            resetAIContext()
          }}
        />
      )}
    </div>
  )
}
